import { Session } from "@iventis/calendar/components/types";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { ScheduleVersion } from "@iventis/domain-model/model/scheduleVersion";
import { utcToZonedTime } from "date-fns-tz";
import { findEventBlockHierarchyEntitiesByCalendarLevel } from "./hierarchy.helpers";
import { FilterType } from "./filter-rules";
import { getCalendarSessionColour, getCalendarSubSessionColour, getEventBlockDisplayValuesFromDataFieldValues } from "./event-block-helpers";
import { getEventRangeClampedToDate } from "./schedule-helper-functions";
import { getCalendarSessionText, ViewType, getViewTypeString } from "./sport-event-helpers";

export const eventBlockHierarchyToCalendarSportSessionViewByDay = (
    eventBlockHierarchy: Hierarchy<EventBlock>[],
    getGroupId: (eventBlock: Hierarchy<EventBlock>) => string,
    scheduleVersionHierarchy: Hierarchy<ScheduleVersion>,
    filterType: FilterType.DayDataField | FilterType.DayVenue,
    viewType: ViewType,
    timezone: string
) => {
    const sessions: Session[] = [];
    findEventBlockHierarchyEntitiesByCalendarLevel(eventBlockHierarchy, 1).forEach((element) => {
        if (getGroupId(element) == null) {
            return;
        }
        const eventBlock = element.item as EventBlock;
        const session: Session = {
            id: eventBlock.id,
            groupId: getGroupId(element),
            sessionStart: utcToZonedTime(eventBlock.startAt, timezone),
            sessionEnd: utcToZonedTime(eventBlock.endAt, timezone),
            displayText: getViewTypeString(eventBlock, scheduleVersionHierarchy, viewType),
            ...getCalendarSessionText(eventBlock, scheduleVersionHierarchy, filterType),
            colour: getCalendarSessionColour(eventBlock),
        };
        const subSessions: Session[] = [];
        findEventBlockHierarchyEntitiesByCalendarLevel(element.children, 2).forEach((subElement) => {
            if (getGroupId(subElement) == null) {
                return;
            }
            subSessions.push(
                eventUnitHierarchyToCalendarSportSubSession(
                    subElement.item,
                    eventBlock,
                    getGroupId(subElement),
                    (eventBlock) => ({
                        start: eventBlock.startAt,
                        end: eventBlock.endAt,
                    }),
                    timezone
                )
            );
        });
        session.subEvents = subSessions;
        sessions.push(session);
    });
    return sessions;
};

/**
 * Create calendar event blocks that all exist in a single day.
 * This is so that they can be displayed with days along the Y axis, meaning regardless of day, they all need to fit into a single day on the X axis.
 */
export const eventBlockHierarchyToCalendarSportSessionGroupedByDay = (
    eventBlockHierarchy: Hierarchy<EventBlock>[],
    scheduleVersionHierarchy: Hierarchy<ScheduleVersion>,
    availableDays: Map<number, Date>,
    getGroupId: (eventBlock: Hierarchy<EventBlock>) => string,
    viewType: ViewType,
    timezone: string
): Session[] => {
    const dayToStuffThemAllInto = Array.from(availableDays.values())[0];
    return findEventBlockHierarchyEntitiesByCalendarLevel(eventBlockHierarchy, 1).map((element) => {
        const eventBlock = element.item as EventBlock;
        const { start, end } = getEventRangeClampedToDate(eventBlock, dayToStuffThemAllInto, timezone);

        const session: Session = {
            id: eventBlock.id,
            groupId: getGroupId(element),
            sessionStart: utcToZonedTime(start, timezone),
            sessionEnd: utcToZonedTime(end, timezone),
            displayText: getViewTypeString(eventBlock, scheduleVersionHierarchy, viewType),
            ...getCalendarSessionText(eventBlock, scheduleVersionHierarchy),
            colour: getCalendarSessionColour(eventBlock),
        };
        const subSessions = [];
        findEventBlockHierarchyEntitiesByCalendarLevel(element.children, 2).forEach((eventUnit) => {
            subSessions.push(
                eventUnitHierarchyToCalendarSportSubSession(
                    eventUnit.item,
                    element.item,
                    getGroupId(eventUnit),
                    (eventBlock) => getEventRangeClampedToDate(eventBlock, dayToStuffThemAllInto, timezone),
                    timezone
                )
            );
        });

        session.subEvents = subSessions;
        return session;
    });
};

const eventUnitHierarchyToCalendarSportSubSession = (
    eventBlock: EventBlock,
    session: EventBlock,
    groupId: string,
    getStartAndEnd: (eventBlock: EventBlock) => { start: Date; end: Date },
    timezone: string
): Session => {
    const { start, end } = getStartAndEnd(eventBlock);
    const subSession: Session = {
        id: eventBlock.id,
        groupId,
        sessionStart: utcToZonedTime(start, timezone),
        sessionEnd: utcToZonedTime(end, timezone),
        name: getEventBlockDisplayValuesFromDataFieldValues(eventBlock.dataFieldValues, eventBlock.type.dataFields).displayText || "No Name Set",
        description: null,
        displayText: null,
        colour: getCalendarSubSessionColour(session, eventBlock),
    };
    return subSession;
};
